import { Component } from '@theme/component';

/**
 * 店舗検索（販売店 / サーキット）の地図・絞り込み。
 *
 * DOM は `sections/store-locator-map-yokomo.liquid` と `snippets/store-card-yokomo.liquid` が出す。
 * この JS は出来上がった DOM を読むだけで、一覧の生成はしない（JS 無効でも一覧が読める作りを保つため）。
 *
 * **店舗の値はすべてカードの `data-*` から読む。JSON は持たない。**
 * 以前は同じ内容の `<script type="application/json" ref="data">` が並んでいたが、462件で 234KB あり、
 * 一覧の HTML と合わせて Shopify の1ファイル 256KB 制限を超えて `theme push` が落ちたため撤去した。
 * 読むキー: `data-store-id` / `data-store-name` / `data-lat` / `data-lng` / `data-mappable` /
 * `data-categories` / `data-area` / `data-prefecture` / `data-prefecture-name` / `data-genres` /
 * `data-circuit-genres` / `data-circuit-types` / `data-search-text`。
 * 増やすときは `snippets/store-card-yokomo.liquid` と sync スクリプトの `render_card()` を両方直す。
 *
 * 種別は1店舗に複数ある（#655。販売店を兼ねるサーキットがある）。
 * `data-categories="dealer circuit"` のように空白区切りの slug で持ち、判定は「含まれるか」で行う。
 * `data-categories` が無いカードだけ、旧来の1値の `data-category` を読む（生成物の移行中の戻り先）。
 *
 * 地図は Leaflet + Leaflet.markercluster（`assets/leaflet*.js` に vendoring 済み）。
 * ビューポートに入るまで読み込まないので、初回描画には一切乗らない。
 *
 * 読む属性（共有契約2）:
 * - `data-preset-category`（`dealer` / `circuit` / `all`）… ページごとに固定の種別。
 *   `all` のときだけ両種別を載せ、訪問者が絞り込みの `cat` で種別を選べる。
 *   生成スニペットは全件を持っているので、その種別を持たない店舗を一覧からも地図からも外す
 *   （兼業の店舗は `dealer` でも `circuit` でも残る）
 * - `data-tile-url` / `data-tile-attribution` … どちらかが空なら地図を初期化せず一覧だけ残す
 * - `data-initial-center="緯度,経度"` / `data-initial-zoom`
 * - `data-asset-base`（任意）… Leaflet 一式の置き場所。省略時は自分自身（`import.meta.url`）と
 *   同じディレクトリ、つまり `assets/` から解決する。importmap には載せない（Leaflet は UMD なので
 *   `window.L` に載る）
 *
 * 書く属性（セクションの CSS が目印にできる。無くても動く）:
 * - ルートの `data-map-state` … `ready` / `error` / `missing-tile-settings`
 * - ルートの `data-map-view` … `list` / `map`。SP で地図と一覧を入れ替えるための状態。
 *   PC は両方出ているので使わない（CSS がメディアクエリで無視する）
 * - カードの `data-store-active` … 一覧とピンの相互ハイライト中
 * - カード内の `[data-store-distance]` … 「約◯km」。無ければ JS が `<p>` を作って追加する
 */

/** URL に出す絞り込みのキー。並び順もこの通り（共有契約3）。 */
export const FILTER_KEYS = Object.freeze(['cat', 'area', 'pref', 'genre', 'type', 'q']);

/** 絞り込みの既定値。この値のキーは URL に出さない。 */
export const EMPTY_FILTERS = Object.freeze({ cat: '', area: '', pref: '', genre: '', type: '', q: '' });

/** 地図が使えないときのフォールバック中心（日本のほぼ中央）。タイル設定とは無関係。 */
const FALLBACK_CENTER = Object.freeze({ lat: 36.2048, lng: 138.2529 });

/**
 * 地図は日本を中心に置き、南北アメリカは日本の東（右）に続けて見せる。
 * 経度 -30° より西（南北アメリカ・ハワイ）は +360° して、ピンを日付変更線の東側に立てる。
 * こうしないとピンは -180〜180 の1枚目の世界（日本の左側のアメリカ）にしか立たず、
 * 日本から東へ回すとアメリカにピンが無い（#600）。距離の計算には元の経度を使う。
 * @param {number} lng
 * @returns {number}
 */
function toPacificLng(lng) {
  return lng < -30 ? lng + 360 : lng;
}

/** 地図を動かせる範囲。日本の西のヨーロッパから、東へずらした南北アメリカまで */
const MAP_BOUNDS = Object.freeze([
  Object.freeze([-85, -40]),
  Object.freeze([85, 340]),
]);
const FALLBACK_ZOOM = 5;

/** 地球の平均半径（km・IUGG）。距離は外部サービスを使わずこの式だけで出す。 */
const EARTH_RADIUS_KM = 6371.0088;

/** vendoring した Leaflet 一式のファイル名。`import.meta.url` からの相対で解決する。 */
/** 現在地の青。iOS の現在地と同じ系統にして、店舗のピン（黒）と役割を分ける。 */
const HERE_BLUE = '#007aff';

/** カードから地図へ飛ばすときの最低ズーム。まとめられたピンの塊で止まらない深さにする。
    13 だと市域が入る程度で、隣の店と同じ塊のままになることがあった */
const FOCUS_ZOOM = 15;

/** 最初に出す件数。1画面に収める。この機能は「現在地から近い順」と地図でも探せるので、
    はじめから多くを積む必要がない */
const REVEAL_STEP = 10;

/** 2回目以降に足す件数。1回目より増やす。押し続ける人は一覧を順に見たい人なので、
    10件ずつだと 394件の末尾まで39回押すことになる */
const REVEAL_MORE_STEP = 20;

const VENDOR_ASSETS = Object.freeze({
  leafletCss: 'leaflet.css',
  leafletJs: 'leaflet.js',
  clusterCss: 'leaflet-markercluster.css',
  clusterJs: 'leaflet-markercluster.js',
  // ベクタータイル用。ラスターを設定しているときは読み込まない（約900KB あるため）
  maplibreCss: 'maplibre-gl.css',
  maplibreJs: 'maplibre-gl.js',
  maplibreLeafletJs: 'leaflet-maplibre-gl.js',
});

/**
 * 設定値がベクタータイルのスタイル定義かどうかを見る。
 * ラスターは `…/{z}/{x}/{y}.png` の形、ベクターはスタイル JSON の URL
 * （例: https://tiles.openfreemap.org/styles/positron）。
 * 見分けを1か所に閉じ込めておけば、設定の書き換えだけで両方に戻せる。
 *
 * @param {string} url
 * @returns {boolean}
 */
function isVectorStyleUrl(url) {
  if (!url) return false;
  return !/\{z\}/.test(url);
}

/**
 * このコンポーネントが自分で付け外しするクラス名・属性。
 * セクション側の CSS はここを目印にできる（無くても動く）。
 */
const CLASSES = Object.freeze({
  pin: 'store-locator-map-yokomo__pin',
  pinActive: 'store-locator-map-yokomo__pin--active',
  pinApproximate: 'store-locator-map-yokomo__pin--approximate',
  here: 'store-locator-map-yokomo__here',
  hereArea: 'store-locator-map-yokomo__here-area',
  distance: 'store-card-yokomo__distance',
  address: 'store-card-yokomo__address',
});

/**
 * ピンの見た目。セクションの CSS が無くても見えるように SVG を直接持つ。
 *
 * 色は YOKOMO のテーマカラー（広い面が黄・点と輪郭が黒）。`fill` / `stroke` は
 * presentation attribute なので、CSS 側（`.store-locator-map-yokomo__pin-body { fill: … }`）が
 * 常に勝つ。ここに書いてあるのは、CSS 変数が取れない環境でも同じ色に見えるためのフォールバック。
 */
const PIN_HTML = [
  '<span class="store-locator-map-yokomo__pin-shape" data-pin aria-hidden="true"',
  ' style="display:block;transform-origin:50% 100%;transition:transform 150ms ease-out">',
  '<svg width="24" height="24" viewBox="0 0 24 24" focusable="false" aria-hidden="true">',
  '<path class="store-locator-map-yokomo__pin-body"',
  ' d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Z"',
  ' fill="#1f1f1f" stroke="#1f1f1f" stroke-width="1.6" stroke-linejoin="round"/>',
  '<circle class="store-locator-map-yokomo__pin-dot" cx="12" cy="9" r="3" fill="#ffffff"/>',
  '</svg></span>',
].join('');

/** ピンの寸法。先端（iconAnchor）が店舗の座標に刺さる。 */
const PIN_SIZE = Object.freeze({ width: 24, height: 24, anchorX: 12, anchorY: 22 });

/**
 * 現在地の「ソナー」。中心の点と、その外へ広がる円（パルス）の2枚。
 * 円の大きさは CSS が持つ（実距離は別に L.circle で描く）。
 */
const CURRENT_POSITION_HTML = [
  '<span class="store-locator-map-yokomo__here-pulse" aria-hidden="true"></span>',
  '<span class="store-locator-map-yokomo__here-dot" aria-hidden="true"></span>',
].join('');

/**
 * 精度の円の最小半径（m）。ブラウザが極端に小さい精度を返しても、
 * 「このあたり」と読める大きさは保つ（点だけになると位置を言い切ってしまう）。
 */
const MIN_ACCURACY_RADIUS_M = 300;

/** 地図の切り替えボタン（SP）の文言。押すたびに入れ替える。 */
// カードの「地図で見る」（Google マップを開く）と紛れないように、切り替えは別の言い方にする
const MAP_TOGGLE_LABELS = Object.freeze({ open: '地図で探す', close: '一覧に戻る' });

/** 位置情報が取れなかったときの案内。一覧は残したまま、これを `ref="status"` に出すだけにする。 */
const GEOLOCATION_MESSAGES = Object.freeze({
  1: '位置情報の利用が許可されませんでした。都道府県で絞り込んでください。',
  2: '現在地を取得できませんでした。都道府県で絞り込んでください。',
  3: '現在地の取得に時間がかかりました。都道府県で絞り込んでください。',
  default: '現在地を取得できませんでした。都道府県で絞り込んでください。',
});

/* ------------------------------------------------------------------ *
 * 純粋関数（DOM に触らない。単体で検証できるようにここへ切り出す）
 * ------------------------------------------------------------------ */

/**
 * 文字列を比較用に正規化する。全角英数・半角カナを NFKC で畳み、小文字化して空白をまとめる。
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeText(value) {
  if (value == null) return '';
  return String(value).normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * キーワードを空白区切りの語に分ける。すべての語を含むものだけを残す（AND）。
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function tokenizeKeyword(value) {
  const normalized = normalizeText(value);
  return normalized ? normalized.split(' ') : [];
}

/**
 * `data-genres="speed drift"` のような半角スペース区切りの値を配列にする（共有契約2）。
 *
 * @param {unknown} value
 * @returns {string[]}
 */
export function splitTokens(value) {
  if (value == null) return [];
  return String(value)
    .split(/\s+/)
    .filter((token) => token !== '');
}

/**
 * カードの種別を配列で読む。`data-categories`（空白区切り）が無いときだけ、旧来の1値の
 * `data-category` を読む（生成物の移行中の戻り先）。
 *
 * @param {DOMStringMap} dataset
 * @returns {string[]} `dealer` / `circuit` の slug。不明なら空配列
 */
export function readCategories(dataset) {
  return splitTokens(dataset.categories ?? dataset.category);
}

/**
 * 緯度経度として使える数値かどうか。
 *
 * @param {unknown} point
 * @returns {point is { lat: number, lng: number }}
 */
function isValidPoint(point) {
  if (!point || typeof point !== 'object') return false;
  const { lat, lng } = /** @type {{ lat: unknown, lng: unknown }} */ (point);
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}

/**
 * 2点間の大円距離（km）。外部サービスを使わずブラウザ内で完結させる。
 *
 * @param {{ lat: number, lng: number } | null | undefined} from
 * @param {{ lat: number, lng: number } | null | undefined} to
 * @returns {number | null} 座標が不正なら null
 */
export function haversineKm(from, to) {
  if (!isValidPoint(from) || !isValidPoint(to)) return null;

  const toRad = Math.PI / 180;
  const dLat = (to.lat - from.lat) * toRad;
  const dLng = (to.lng - from.lng) * toRad;
  const lat1 = from.lat * toRad;
  const lat2 = to.lat * toRad;

  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * @typedef {Object} StoreFilterable
 * @property {string[]} categories `dealer` / `circuit`。兼業の店舗は両方持つ
 * @property {string} area
 * @property {string} prefecture JIS 2桁の文字列
 * @property {string[]} sellGenres `data-genres`。販売店として「取り扱っている」ジャンル
 * @property {string[]} runGenres `data-circuit-genres`。サーキットとして「走れる」ジャンル
 * @property {string[]} circuitTypes
 * @property {string} searchText `normalizeText` 済みの検索対象文字列
 */

/**
 * @typedef {{ cat: string, area: string, pref: string, genre: string, type: string, q: string }} StoreFilters
 */

/**
 * 1店舗がジャンルで当たるか。ジャンルの軸は「どの種別として見ているか」で変わる。
 * 販売店としては `sellGenres`、サーキットとしては `runGenres`。
 * この2つは包含関係にない（68サーキット中34件でズレ、うち10件は包含関係も無い）。混ぜると、
 * 走れないジャンルで絞ったコースが出てしまう。
 *
 * @param {StoreFilterable} store
 * @param {string} genre
 * @param {string} [role] 見ている種別（`dealer` / `circuit`）。空なら店舗が持つ種別のどれかで当たればよい
 * @returns {boolean}
 */
export function matchesGenre(store, genre, role = '') {
  if (!genre) return true;
  // 種別が決まっているときは、その種別を持つ店舗しか残っていない（種別の判定が先に外す）
  if (role === 'circuit') return store.runGenres.includes(genre);
  if (role === 'dealer') return store.sellGenres.includes(genre);

  const asDealer = store.categories.includes('dealer') && store.sellGenres.includes(genre);
  const asCircuit = store.categories.includes('circuit') && store.runGenres.includes(genre);
  return asDealer || asCircuit;
}

/**
 * 1店舗が絞り込み条件を満たすか。条件はすべて AND。
 *
 * @param {StoreFilterable} store
 * @param {StoreFilters} filters
 * @param {string} [preset] ページで固定の種別（`data-preset-category`）。`all` や空なら固定なし。
 *   種別での除外は `#readEntries` が済ませているので、ここではジャンルの軸を決めるためだけに使う
 * @returns {boolean}
 */
export function matchesFilters(store, filters, preset = '') {
  // 種別。`all` は「両方」の意味なので絞らない（`type` は屋内・屋外が使っているので別キー）
  const cat = filters.cat && filters.cat !== 'all' ? filters.cat : '';
  if (cat && !store.categories.includes(cat)) return false;
  if (filters.area && store.area !== filters.area) return false;
  if (filters.pref && store.prefecture !== filters.pref) return false;

  const fixedPreset = preset && preset !== 'all' ? preset : '';
  if (!matchesGenre(store, filters.genre, fixedPreset || cat)) return false;
  if (filters.type && !store.circuitTypes.includes(filters.type)) return false;

  const terms = tokenizeKeyword(filters.q);
  if (terms.length && !terms.every((term) => store.searchText.includes(term))) return false;

  return true;
}

/**
 * 絞り込みを `URLSearchParams` にする。既定値のキーは出さず、関係ないキーはそのまま残す。
 *
 * @param {StoreFilters} filters
 * @param {URLSearchParams} [base] 現在の URL のクエリ（渡しても破壊しない）
 * @returns {URLSearchParams}
 */
export function serializeFilters(filters, base = new URLSearchParams()) {
  const params = new URLSearchParams(base);

  for (const key of FILTER_KEYS) {
    const value = String(filters?.[key] ?? '').trim();
    if (value) params.set(key, value);
    else params.delete(key);
  }

  return params;
}

/**
 * `URLSearchParams` から絞り込みを復元する。知らないキーは無視する。
 *
 * @param {URLSearchParams} params
 * @returns {StoreFilters}
 */
export function parseFilters(params) {
  const filters = { ...EMPTY_FILTERS };

  for (const key of FILTER_KEYS) {
    filters[key] = (params?.get(key) ?? '').trim();
  }

  return filters;
}

/**
 * カードに出す距離の文言。10km 未満は小数第1位まで、それ以上は整数に丸める。
 *
 * @param {number | null | undefined} km
 * @returns {string} 距離が無いときは空文字
 */
export function formatDistanceLabel(km) {
  if (typeof km !== 'number' || !Number.isFinite(km)) return '';
  return km < 10 ? `約${km.toFixed(1)}km` : `約${Math.round(km)}km`;
}

/**
 * @typedef {Object} RankableStore
 * @property {unknown} id
 * @property {number | null} lat
 * @property {number | null} lng
 * @property {boolean} mappable
 * @property {boolean} [approximate] 国の代表点しか無い（海外32件）
 */

/**
 * 現在地から近い順に並べ替える。
 * 座標を持たない店舗と、国の代表点しか無い店舗（海外32件）は順位を付けず、
 * 元の並びのまま末尾に残す。国の重心までの距離は「近い店」の答えにならない。
 *
 * @param {RankableStore[]} stores
 * @param {{ lat: number, lng: number } | null | undefined} origin
 * @returns {(RankableStore & { distanceKm: number | null })[]} 入力は破壊しない
 */
export function rankByDistance(stores, origin) {
  const source = [...stores];

  if (!isValidPoint(origin)) {
    return source.map((store) => ({ ...store, distanceKm: null }));
  }

  /** @type {(RankableStore & { distanceKm: number })[]} */
  const measured = [];
  /** @type {(RankableStore & { distanceKm: null })[]} */
  const unmeasured = [];

  for (const store of source) {
    const rankable = store.mappable !== false && store.approximate !== true;
    const distanceKm = rankable ? haversineKm(origin, { lat: store.lat, lng: store.lng }) : null;

    if (distanceKm == null) unmeasured.push({ ...store, distanceKm: null });
    else measured.push({ ...store, distanceKm });
  }

  measured.sort((a, b) => a.distanceKm - b.distanceKm);

  return [...measured, ...unmeasured];
}

/* ------------------------------------------------------------------ *
 * vendoring したアセットの遅延読み込み
 * ------------------------------------------------------------------ */

/** @type {Map<string, Promise<void>>} 同じ URL を二重に読み込まない（セクションが同一ページに2つ置かれても1回） */
const assetPromises = new Map();

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
  const cached = assetPromises.get(src);
  if (cached) return cached;

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error(`store-locator: failed to load ${src}`)), { once: true });
    document.head.appendChild(script);
  });

  assetPromises.set(src, promise);
  return promise;
}

/**
 * スタイルは失敗しても地図の初期化は続ける（見た目が崩れても一覧は残す方針）。
 *
 * @param {string} href
 * @returns {Promise<void>}
 */
function loadStylesheet(href) {
  const cached = assetPromises.get(href);
  if (cached) return cached;

  const promise = new Promise((resolve) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
    document.head.appendChild(link);
  });

  assetPromises.set(href, promise);
  return promise;
}

/* ------------------------------------------------------------------ *
 * カスタム要素
 * ------------------------------------------------------------------ */

/**
 * @typedef {Object} StoreEntry
 * @property {number} index
 * @property {string} id
 * @property {string} name
 * @property {string[]} categories `dealer` / `circuit`。`data-preset-category` が含まれるかを見る
 * @property {string} prefectureName 都道府県の表示名（選択肢の組み立てに使う）。海外は空
 * @property {HTMLElement} item 一覧の `<li>`
 * @property {HTMLElement} card `[data-store-id]` を持つカード
 * @property {number | null} lat
 * @property {number | null} lng
 * @property {number | null} mapLng 地図に立てる経度（`toPacificLng` 済み）
 * @property {boolean} mappable
 * @property {boolean} approximate 座標が国の代表点しか無い（海外32件）
 * @property {StoreFilterable} filterable
 * @property {any} marker Leaflet の Marker（地図の初期化後にだけ入る）
 * @property {number | null} distanceKm
 * @property {boolean} visible
 */

/**
 * @typedef {Object} StoreLocatorRefs
 * @property {HTMLElement} list
 * @property {HTMLElement} count
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [map]
 * @property {HTMLSelectElement} [categorySelect] 種別（`data-preset-category="all"` のときだけある）
 * @property {HTMLSelectElement} [areaSelect]
 * @property {HTMLSelectElement} [prefectureSelect]
 * @property {HTMLSelectElement} [genreSelect]
 * @property {HTMLSelectElement} [circuitTypeSelect]
 * @property {HTMLInputElement} [keyword]
 * @property {HTMLButtonElement} [nearestButton]
 * @property {HTMLButtonElement} [revealButton]
 * @property {HTMLButtonElement} [clearButton]
 * @property {HTMLButtonElement} [mapToggle] SP で地図を出す切り替え（PC では CSS が隠す）
 */

/**
 * @extends {Component<StoreLocatorRefs>}
 */
/**
 * 一覧の残りのページを Section Rendering API で取り、`list` に足す（#656）。
 * Liquid は store メタオブジェクトを1ページ250件までしか出せないため。
 * ⚠️ ページ送りのパラメータ名は `page` ではなく `page_xxxxxxxx` の形（ストア側が決める）。
 *    名前を組み立てず、サーバーが出した `data-next-url` / `data-previous-url` を辿る。
 * セクション id は器を包む `.shopify-section` から取る。
 *
 * @param {HTMLElement} host
 * @param {HTMLElement} list
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
export async function loadRemainingStorePages(host, list, signal) {
  const section = host.closest('.shopify-section');
  const sectionId = section?.id?.replace(/^shopify-section-/, '');
  if (!list || !sectionId) return;

  /** @param {string} path */
  const fetchList = async (path) => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    try {
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return /** @type {HTMLElement | null} */ (doc.querySelector('store-locator-yokomo-component [ref="list"]'));
    } catch {
      return null;
    }
  };

  // 辿るページ数の上限（250件 × 20 = 5,000件）。URL が循環したときに止めるための保険
  const MAX_PAGES = 20;
  for (const direction of ['nextUrl', 'previousUrl']) {
    let path = list.dataset[direction];
    for (let count = 0; path && count < MAX_PAGES && !signal.aborted; count += 1) {
      const remote = await fetchList(path);
      if (!remote) break;
      list.append(...[...remote.children].map((item) => document.importNode(item, true)));
      path = remote.dataset[direction];
    }
  }
}

/**
 * 一覧の `<li>` を「都道府県（海外・不明は最後） → 店名 → 種別（dealer → circuit） → id」に並べ直す。
 * Liquid はメタオブジェクトを並べ替えられないので、ここで決める（旧生成スクリプトの sort_key と同じ順）。
 *
 * @param {HTMLElement} list
 */
export function sortStoreItems(list) {
  if (!list) return;
  const keyOf = (item) => {
    const card = /** @type {HTMLElement} */ (item.querySelector('[data-store-id]') ?? item);
    const code = card.dataset.prefecture ?? '';
    const categories = (card.dataset.categories ?? '').split(' ');
    return {
      overseas: code === '' ? 1 : 0,
      code,
      name: card.dataset.storeName ?? '',
      category: categories.includes('dealer') ? (categories.includes('circuit') ? 1 : 0) : 2,
      id: card.dataset.storeId ?? '',
    };
  };
  const compareText = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
  const items = [...list.children].map((item) => ({ item, key: keyOf(item) }));
  items.sort(
    (a, b) =>
      a.key.overseas - b.key.overseas ||
      compareText(a.key.code, b.key.code) ||
      compareText(a.key.name, b.key.name) ||
      a.key.category - b.key.category ||
      compareText(a.key.id, b.key.id)
  );
  list.append(...items.map(({ item }) => item));
}

export class StoreLocatorYokomoComponent extends Component {
  requiredRefs = ['list', 'count'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {StoreEntry[]} */
  #entries = [];
  /** @type {StoreFilters} */
  #filters = { ...EMPTY_FILTERS };
  /** @type {Map<string, string>} 都道府県コード → 地域。地域を選んだときに都道府県の選択肢を連動させる */
  #prefectureAreas = new Map();
  /** @type {WeakMap<Element, StoreEntry>} */
  #entryByCard = new WeakMap();
  /** @type {Map<any, StoreEntry>} */
  #entryByMarker = new Map();
  /** @type {IntersectionObserver | null} */
  #mapObserver = null;
  /** @type {any} Leaflet の Map */
  #map = null;
  /** @type {any} Leaflet.markercluster の MarkerClusterGroup */
  #cluster = null;

  /** 段階表示でいま出している上限。スマホで入れ子スクロールを避けるために使う */
  #revealed = REVEAL_STEP;

  /** @type {boolean} 選択肢を変えた直後だけ、次の同期で地図を結果へ寄せる */
  #fitVisibleOnce = false;

  /** ホイールの案内を消すタイマー */
  #wheelHintTimer = 0;
  #mapStarted = false;
  /** @type {StoreEntry | null} */
  #activeEntry = null;
  /** @type {any} 吹き出しを開いている Marker。同時に1つだけであることを明示的に保証するために持つ */
  #openPopupMarker = null;
  /** @type {{ lat: number, lng: number } | null} 取得できた現在地。地図より先に取れることがあるので持っておく */
  #origin = null;
  /** @type {number | null} 現在地の精度（m）。円の半径に使う */
  #originAccuracyM = null;
  /** @type {any} 現在地のソナー（中心の点） */
  #hereMarker = null;
  /** @type {any} 現在地の精度の円（実距離） */
  #hereCircle = null;
  /** 浮かせたパネルぶんの中心補正は初期化時の1回だけ（押すたびにずれていかないように） */
  #panelOffsetApplied = false;
  /** @type {number} */
  #filterTimer = 0;
  /** @type {number} */
  #urlTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort?.abort();
    this.#abort = new AbortController();

    // SP は一覧だけで始める（地図は切り替えボタンを押したときに初めて出す）
    if (!this.dataset.mapView) this.dataset.mapView = 'list';

    // 一覧は store メタオブジェクトを Liquid が250件ずつ出す（#656）。残りのページを足して
    // 並べ直してから組み立てる。取れなかったページがあっても、手元の分で動かす
    const signal = this.#abort.signal;
    loadRemainingStorePages(this, this.refs.list, signal).then(() => {
      if (signal.aborted) return;
      sortStoreItems(this.refs.list);
      this.#start();
    });
  }

  /** 一覧がそろってからの初期化 */
  #start() {
    this.#entries = this.#readEntries();
    this.#prefectureAreas = this.#buildPrefectureAreaMap();
    this.#populatePrefectureOptions();

    this.#filters = parseFilters(new URLSearchParams(window.location.search));
    this.#syncControlsFromFilters();
    this.#applyFilters();

    this.#setupFilterListeners();
    this.#setupListInteractions();
    this.#setupGeolocation();
    this.#setupReveal();
    this.#setupMapToggle();
    this.#observeMapViewport();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#mapObserver?.disconnect();
    this.#mapObserver = null;
    window.clearTimeout(this.#filterTimer);
    window.clearTimeout(this.#urlTimer);

    try {
      this.#map?.remove();
    } catch {
      // 破棄に失敗しても後続に影響しない
    }
    this.#map = null;
    this.#cluster = null;
    this.#hereMarker = null;
    this.#hereCircle = null;
    this.#entryByMarker.clear();
  }

  /* -------------------------------------------------- 読み取り */

  /**
   * 一覧の `<li>` から内部表現を作る。値の出どころはカードの `data-*` だけ（共有契約2）。
   *
   * @returns {StoreEntry[]}
   */
  #readEntries() {
    // 種別はページごとに固定（共有契約2）。合わない店舗を外すのに使うので先に読む。
    const presetCategory = (this.dataset.presetCategory ?? '').trim();

    const list = this.refs.list;
    let items = /** @type {HTMLElement[]} */ ([...list.querySelectorAll(':scope > li')]);
    if (!items.length) {
      items = /** @type {HTMLElement[]} */ (
        [...list.querySelectorAll('[data-store-id]')].map((card) => card.closest('li') ?? card)
      );
    }

    const entries = items.map((item, index) => {
      const card = /** @type {HTMLElement} */ (item.querySelector('[data-store-id]') ?? item);
      const id = card.dataset.storeId || `index-${index}`;

      const lat = toFiniteNumber(card.dataset.lat);
      const lng = toFiniteNumber(card.dataset.lng);
      const mappable = card.dataset.mappable !== 'false' && lat != null && lng != null;
      // 座標が国の代表点しか無い店舗（海外32件）。ピンは立てるが所在地そのものではない
      const approximate = mappable && card.dataset.approximate === 'true';

      // ジャンルは2軸とも持っておき、どちらで見るかは絞り込みのたびに決める（`matchesGenre`）。
      // data-genres         = 販売店として「取り扱っている」ジャンル
      // data-circuit-genres = サーキットとして「走れる」ジャンル
      const categories = readCategories(card.dataset);
      const sellGenres = splitTokens(card.dataset.genres);
      const runGenres = splitTokens(card.dataset.circuitGenres);
      const circuitTypes = splitTokens(card.dataset.circuitTypes);

      /** @type {StoreEntry} */
      const entry = {
        index,
        id,
        name: (card.dataset.storeName ?? '').trim(),
        categories,
        prefectureName: (card.dataset.prefectureName ?? '').trim(),
        item,
        card,
        lat,
        lng,
        mapLng: lng == null ? null : toPacificLng(lng),
        mappable,
        approximate,
        filterable: {
          categories,
          area: card.dataset.area ?? '',
          prefecture: card.dataset.prefecture ?? '',
          sellGenres,
          runGenres,
          circuitTypes,
          searchText: normalizeText(card.dataset.searchText),
        },
        marker: null,
        distanceKm: null,
        visible: true,
      };

      this.#entryByCard.set(card, entry);
      return entry;
    });

    // 種別（販売店 / サーキット）はページごとに固定で、訪問者は切り替えられない（共有契約2）。
    // 生成スニペットは全件（dealer + circuit）を持っているので、ここでその種別を持たない分を
    // 一覧からも地図からも外す。兼業の店舗はどちらの種別のページにも残る。
    // JavaScript が無効なときだけ全件が読める形で残る。
    // `all` は両種別を残し、種別は絞り込み（`cat`）に任せる。
    const preset = presetCategory === 'all' ? '' : presetCategory;

    // 表示する店舗を handle で限定するページ（ロイヤリティ LP の「走行券と交換できるサーキット」）。
    // 空なら限定しない。リストに無い店舗は種別と同じく最初から外すので、件数・絞り込み・
    // 近い順の並べ替え・地図のピンもこの店舗だけを母数にする。
    // 管理画面ではカンマ区切りで入れる（全角・読点も許す）ので、空白区切りに直してから分ける
    const onlyStores = new Set(splitTokens((this.dataset.onlyStores ?? '').replace(/[,、，]/g, ' ')));

    if (!preset && !onlyStores.size) return entries;

    /** @type {StoreEntry[]} */
    const kept = [];

    for (const entry of entries) {
      // 種別が読めない店舗は外さない（以前の1値の判定と同じ）
      const outOfPreset = Boolean(preset && entry.categories.length && !entry.categories.includes(preset));
      const outOfList = onlyStores.size > 0 && !onlyStores.has(entry.id);
      if (outOfPreset || outOfList) {
        entry.item.hidden = true;
        // `hidden` は display 指定に負けるので、インラインでも落としておく
        entry.item.style.display = 'none';
        // 限定で外したカードはカード自身にも hidden を付ける。絞り込みは `<li>` しか触らないので、
        // カード単位で数える検証（`.store-card-yokomo:not([hidden])`）が外した分を数えないように
        if (outOfList) entry.card.hidden = true;
        this.#entryByCard.delete(entry.card);
        continue;
      }

      // `#sortByCurrentPosition` が `#entries` の添字として使うので振り直す
      entry.index = kept.length;
      kept.push(entry);
    }

    return kept;
  }

  /**
   * 都道府県コード → 地域の対応を実データから作る。
   * 選択肢が `data-area` を持っていればそちらを優先するので、これは無くても困らない保険。
   *
   * @returns {Map<string, string>}
   */
  #buildPrefectureAreaMap() {
    const map = new Map();

    for (const entry of this.#entries) {
      const { prefecture, area } = entry.filterable;
      if (prefecture && area && !map.has(prefecture)) map.set(prefecture, area);
    }

    return map;
  }

  /* -------------------------------------------------- 絞り込み */

  #setupFilterListeners() {
    const options = { signal: /** @type {AbortSignal} */ (this.#abort?.signal) };
    const { categorySelect, areaSelect, prefectureSelect, genreSelect, circuitTypeSelect, keyword, clearButton } =
      this.refs;

    categorySelect?.addEventListener(
      'change',
      () => {
        this.#filters.cat = categorySelect.value;
        this.#fitVisibleOnce = true;
        this.#commit();
      },
      options
    );

    areaSelect?.addEventListener(
      'change',
      () => {
        this.#filters.area = areaSelect.value;
        this.#fitVisibleOnce = true;
        this.#syncPrefectureOptions();
        this.#commit();
      },
      options
    );

    prefectureSelect?.addEventListener(
      'change',
      () => {
        this.#filters.pref = prefectureSelect.value;
        this.#fitVisibleOnce = true;
        this.#commit();
      },
      options
    );

    genreSelect?.addEventListener(
      'change',
      () => {
        this.#filters.genre = genreSelect.value;
        this.#fitVisibleOnce = true;
        this.#commit();
      },
      options
    );

    circuitTypeSelect?.addEventListener(
      'change',
      () => {
        this.#filters.type = circuitTypeSelect.value;
        this.#fitVisibleOnce = true;
        this.#commit();
      },
      options
    );

    keyword?.addEventListener(
      'input',
      () => {
        this.#filters.q = keyword.value;
        // 打鍵ごとに履歴を積むと戻るボタンが使い物にならないので、URL の書き込みだけ遅らせる
        window.clearTimeout(this.#filterTimer);
        this.#filterTimer = window.setTimeout(() => this.#applyFilters(), 120);
        this.#scheduleUrlUpdate(400);
      },
      options
    );

    keyword?.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') return;
        // 親フォームがあっても送信させない（絞り込みはその場で終わる）
        event.preventDefault();
        this.#filters.q = keyword.value;
        this.#commit();
      },
      options
    );

    clearButton?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#filters = { ...EMPTY_FILTERS };
        this.#syncControlsFromFilters();
        this.#commit();
        this.refs.keyword?.focus();
      },
      options
    );

    window.addEventListener(
      'popstate',
      () => {
        this.#filters = parseFilters(new URLSearchParams(window.location.search));
        this.#syncControlsFromFilters();
        this.#applyFilters();
      },
      options
    );
  }

  /** 絞り込みを反映し、URL も更新する（即時） */
  #commit() {
    window.clearTimeout(this.#filterTimer);
    this.#applyFilters();
    this.#scheduleUrlUpdate(0);
  }

  /**
   * @param {number} delay
   */
  #scheduleUrlUpdate(delay) {
    window.clearTimeout(this.#urlTimer);
    this.#urlTimer = window.setTimeout(() => this.#updateUrl(), delay);
  }

  #updateUrl() {
    const url = new URL(window.location.href);
    url.search = serializeFilters(this.#filters, url.searchParams).toString();

    const next = url.toString();
    if (next === window.location.href) return;

    history.pushState({ storeLocatorFilters: this.#filters }, '', next);
  }

  /** 一覧・地図・件数を現在の絞り込みに合わせる */
  #applyFilters({ resetReveal = true } = {}) {
    if (resetReveal) this.#revealed = REVEAL_STEP;

    let visible = 0;
    let shown = 0;
    // 固定の種別のページでは、ジャンルをその種別の軸で見る（`matchesGenre`）
    const preset = (this.dataset.presetCategory ?? '').trim();

    for (const entry of this.#entries) {
      const match = matchesFilters(entry.filterable, this.#filters, preset);
      entry.visible = match;
      if (match) visible += 1;

      // 絞り込みに合っていても、段階表示の枠を超えた分は出さない。
      // 枠を使うのは一覧が入れ子スクロールでないとき（スマホ）だけ。
      // PC は枠の中で好きなだけ送れるので全部出す。
      const withinReveal = !this.#usesReveal() || shown < this.#revealed;
      const show = match && withinReveal;
      if (show) shown += 1;

      entry.item.hidden = !show;
      // `hidden` は display 指定に負けるので、インラインでも落としておく
      entry.item.style.display = show ? '' : 'none';
    }

    // 件数は「絞り込みに合った数」。いま出している数ではない。
    if (this.refs.count) this.refs.count.textContent = `${visible}件`;
    if (this.refs.empty) this.refs.empty.hidden = visible !== 0;

    this.#syncRevealButton(visible, shown);

    if (this.#activeEntry && !this.#activeEntry.visible) this.#setActiveEntry(null);

    this.#syncMarkers();
  }

  /**
   * 段階表示を使うか。一覧が自前のスクロール領域を持っていない＝ページのスクロールに
   * そのまま乗る状態（スマホ）のときだけ使う。
   *
   * 入れ子の縦スクロールはタッチでは指の置き場所で挙動が変わり、
   * 内側を末端まで送らないと先へ進めない。スクロール面を1つに保つ。
   */
  #usesReveal() {
    const list = this.refs.list;
    if (!list) return false;
    return getComputedStyle(list).overflowY !== 'auto';
  }

  /**
   * 「もっと見る」の出し入れ。残り件数を文言に出して、あと何件あるか分かるようにする。
   *
   * @param {number} matched 絞り込みに合った件数
   * @param {number} shown いま出している件数
   */
  #syncRevealButton(matched, shown) {
    const button = this.refs.revealButton;
    if (!button) return;

    const rest = matched - shown;
    if (rest <= 0) {
      button.hidden = true;
      return;
    }

    button.hidden = false;
    button.textContent = `もっと見る（あと${rest}件）`;
  }

  /**
   * 都道府県の選択肢をデータから組み立てる。
   * Liquid 側は「すべての都道府県」の1件しか出さない（データに無い県を出さないため）ので、
   * 実在する県だけをここで足す。海外（`prefecture` が空）は選択肢を作らない。
   */
  #populatePrefectureOptions() {
    const select = this.refs.prefectureSelect;
    if (!select) return;

    /** @type {Map<string, string>} JIS 2桁コード → 表示名 */
    const names = new Map();

    for (const entry of this.#entries) {
      const code = entry.filterable.prefecture;
      if (!code || names.has(code)) continue;
      names.set(code, entry.prefectureName || code);
    }

    const existing = new Set([...select.options].map((option) => option.value));
    const fragment = document.createDocumentFragment();

    // JIS コード順 = 北から南の並び
    for (const code of [...names.keys()].sort()) {
      if (existing.has(code)) continue;

      const option = document.createElement('option');
      option.value = code;
      option.textContent = names.get(code) ?? code;

      const area = this.#prefectureAreas.get(code);
      if (area) option.dataset.area = area;

      fragment.appendChild(option);
    }

    select.appendChild(fragment);
  }

  /** 地域に合わせて都道府県の選択肢を絞る */
  #syncPrefectureOptions() {
    const select = this.refs.prefectureSelect;
    if (!select) return;

    const area = this.#filters.area;

    for (const option of select.options) {
      if (!option.value) {
        option.hidden = false;
        option.disabled = false;
        continue;
      }

      const optionArea = option.dataset.area || this.#prefectureAreas.get(option.value) || '';
      // 対応が分からない選択肢は隠さない（データに1件も無い県で選べなくなるのを避ける）
      const show = !area || !optionArea || optionArea === area;

      option.hidden = !show;
      option.disabled = !show;
    }

    if (select.selectedOptions[0]?.disabled) {
      select.value = '';
      this.#filters.pref = '';
    }
  }

  /** URL から復元した絞り込みを、実際のコントロールへ書き戻す */
  #syncControlsFromFilters() {
    const { categorySelect, areaSelect, prefectureSelect, genreSelect, circuitTypeSelect, keyword } = this.refs;

    /**
     * @param {HTMLSelectElement | undefined} select
     * @param {keyof StoreFilters} key
     */
    const applySelect = (select, key) => {
      if (!select) return;
      const value = this.#filters[key];
      const known = [...select.options].some((option) => option.value === value);
      // URL に知らない値が入っていたら既定に落とす（選択肢と表示がずれないように）
      if (!known) this.#filters[key] = '';
      select.value = this.#filters[key];
    };

    // 種別が固定のページ（`dealer` / `circuit`）には種別の選択肢が無い。URL の `cat` で
    // 固定の種別をさらに絞ると0件になるので、選択肢が無ければ既定に落とす
    if (categorySelect) applySelect(categorySelect, 'cat');
    else this.#filters.cat = '';
    applySelect(areaSelect, 'area');
    this.#syncPrefectureOptions();
    applySelect(prefectureSelect, 'pref');
    applySelect(genreSelect, 'genre');
    applySelect(circuitTypeSelect, 'type');

    if (keyword) keyword.value = this.#filters.q;
  }

  /* -------------------------------------------------- 一覧と地図の連動 */

  #setupListInteractions() {
    const options = { signal: /** @type {AbortSignal} */ (this.#abort?.signal) };
    const list = this.refs.list;

    // 462件ぶんの listener を張らずに済むよう、一覧側で受けて委譲する
    list.addEventListener('mouseover', (event) => this.#setActiveEntry(this.#entryFromEvent(event)), options);
    list.addEventListener('focusin', (event) => this.#setActiveEntry(this.#entryFromEvent(event)), options);
    list.addEventListener('mouseleave', () => this.#setActiveEntry(null), options);

    // カードを押したら、その店のピンまで地図を寄せて吹き出しを開く。
    // リンク（ウェブサイト / 地図で見る / ここへ行く）の上は素通りさせる。
    // ここで拾うと外部サイトや Google マップへ飛べなくなる。
    list.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.closest('a, button')) return;
        const entry = this.#entryFromEvent(event);
        if (entry) this.#focusEntryOnMap(entry);
      },
      options
    );
    list.addEventListener(
      'focusout',
      (event) => {
        const next = /** @type {Node | null} */ (event.relatedTarget);
        if (!next || !list.contains(next)) this.#setActiveEntry(null);
      },
      options
    );
  }

  /**
   * 一覧のカードから地図側を動かす。クラスタにまとめられている場合は、
   * 先にクラスタを開いてからピンを出す（開かないと吹き出しの相手がいない）。
   *
   * @param {StoreEntry} entry
   */
  #focusEntryOnMap(entry) {
    this.#setActiveEntry(entry);

    const map = this.#map;
    if (!map || !entry.mappable || entry.lat == null || entry.lng == null) return;

    const target = [entry.lat, entry.mapLng];
    // 国の代表点（海外）は寄っても何も無いので、その手前で止める
    const floor = entry.approximate ? 5 : FOCUS_ZOOM;
    const zoom = Math.max(map.getZoom(), floor);

    const open = () => {
      try {
        entry.marker?.openPopup?.();
        this.#openPopupMarker = entry.marker ?? null;
      } catch {
        /* 吹き出しが出せなくても、地図の移動は済んでいる */
      }
    };

    // 寄せ終わってからクラスタをほどく。すでに単独ならそのまま吹き出しが開く。
    const focus = () => {
      if (entry.marker && typeof this.#cluster?.zoomToShowLayer === 'function') {
        this.#cluster.zoomToShowLayer(entry.marker, open);
      } else {
        open();
      }
    };

    // ⚠️ `zoomToShowLayer` と `setView` を同時に呼ばない。
    // 以前は両方を並べて呼んでいたため、`setView` に渡す縮尺が**動く前**の
    // `map.getZoom()` を見ていて、クラスタがほどけた直後に浅い縮尺へ戻していた。
    // 結果、カードを押した先がまた塊のままになる（実測でそうなっていた）。
    map.once('moveend', focus);
    map.setView(target, zoom, { animate: true });
  }

  /**
   * @param {Event} event
   * @returns {StoreEntry | null}
   */
  #entryFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const card = target.closest('[data-store-id]');
    return card ? this.#entryByCard.get(card) ?? null : null;
  }

  /**
   * @param {StoreEntry | null} entry
   */
  #setActiveEntry(entry) {
    if (this.#activeEntry === entry) return;

    if (this.#activeEntry) {
      this.#activeEntry.card.removeAttribute('data-store-active');
      this.#toggleMarkerHighlight(this.#activeEntry, false);
    }

    this.#activeEntry = entry;

    if (entry) {
      entry.card.setAttribute('data-store-active', 'true');
      this.#toggleMarkerHighlight(entry, true);
    }
  }

  /**
   * ピンを目立たせる。クラスタに畳まれているときは、代わりに見えている親（クラスタ）を光らせる。
   *
   * Leaflet はピンの位置決めに `_icon.style.transform` を使うので、そこには一切触らない。
   *
   * @param {StoreEntry} entry
   * @param {boolean} active
   */
  #toggleMarkerHighlight(entry, active) {
    if (!entry.marker || !this.#map) return;

    const layer = this.#cluster?.getVisibleParent?.(entry.marker) ?? entry.marker;
    const icon = layer?._icon;
    if (!icon) return;

    icon.classList.toggle(CLASSES.pinActive, active);
    icon.style.zIndex = active ? '1000' : '';
    icon.style.filter = active ? 'drop-shadow(0 0 4px rgba(0, 0, 0, 0.55))' : '';

    const shape = icon.querySelector('[data-pin]');
    if (shape) shape.style.transform = active ? 'scale(1.3)' : '';
  }

  /**
   * @param {StoreEntry} entry
   */
  #scrollToEntry(entry) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // 段階表示（「もっと見る」）の枠の外にある店は、まだ一覧に出ていない。そのまま送ると
    // いま出ている最後のカードで止まる（#600）。その店が出るところまで枠を広げてから送る
    this.#revealEntry(entry);

    // 一覧のカードは画面外では描画を省いていて（`content-visibility: auto`）、仮の高さで並んでいる。
    // なめらかに送ると、通り過ぎたカードが途中で本来の高さに伸び、行き先が下へ逃げて手前で止まる（#600）。
    // 遠くへ飛ぶときは一気に送り、描画が落ち着いてからもう一度合わせる。近くならなめらかに送る
    const list = this.refs.list;
    const nested = !!list && getComputedStyle(list).overflowY === 'auto';
    const viewTop = nested ? list.getBoundingClientRect().top : 0;
    const viewHeight = nested ? list.clientHeight : window.innerHeight;
    const far = Math.abs(entry.item.getBoundingClientRect().top - viewTop) > viewHeight * 2;

    // 押した店は枠の先頭に出す。'nearest' だと下へ送ったとき枠の最下端に来るだけで、
    // 枠の大半を直前に並ぶ別の店（別の国）が占め、押した店と違う店が出たように見える
    /** @param {ScrollBehavior} behavior */
    const scroll = (behavior) => {
      if (!nested || !list) {
        entry.item.scrollIntoView({ block: 'start', inline: 'nearest', behavior });
        return;
      }
      // PC は一覧の枠の中だけを送る。scrollIntoView は祖先を全部送るので、ページ全体まで動いてしまう
      const margin = Number.parseFloat(getComputedStyle(entry.item).scrollMarginTop) || 0;
      const offset = entry.item.getBoundingClientRect().top - list.getBoundingClientRect().top;
      list.scrollTo({ top: list.scrollTop + offset - margin, behavior });
    };

    scroll(reduceMotion || far ? 'auto' : 'smooth');
    if (!far) return;

    // 行き先の周りのカードが本来の高さで描かれるのを2フレーム待ってから、位置を合わせ直す
    requestAnimationFrame(() => requestAnimationFrame(() => scroll('auto')));
  }

  /**
   * 絞り込みに合っているのに段階表示の枠の外にある店を、一覧に出す。
   * 枠は「もっと見る」と同じ刻みで広げる（押した店の後ろにも続きが並ぶようにする）。
   *
   * @param {StoreEntry} entry
   */
  #revealEntry(entry) {
    if (!entry.visible || !entry.item.hidden || !this.#usesReveal()) return;

    const rank = this.#entries.filter((other) => other.visible).indexOf(entry) + 1;
    if (rank <= 0) return;

    while (this.#revealed < rank) this.#revealed += REVEAL_MORE_STEP;
    this.#applyFilters({ resetReveal: false });
  }

  /* -------------------------------------------------- 現在地 */

  /**
   * タッチでは指2本のときだけ地図を操作できるようにする。
   *
   * 指1本のドラッグで地図が動くと、ページを読み進めようとしただけで地図が流れ、
   * スクロールが奪われる。Google マップのスマホ版と同じ作法にそろえる。
   * ピンチ（2本指）での拡大は Leaflet の touchZoom がそのまま担う。
   *
   * @param {any} map
   * @param {HTMLElement} container
   */
  #setupTwoFingerPan(map, container) {
    if (!('ontouchstart' in window)) return;

    const options = { signal: /** @type {AbortSignal} */ (this.#abort?.signal), passive: true };
    map.dragging.disable();

    container.addEventListener('touchstart', (event) => {
      if (event.touches.length >= 2) {
        map.dragging.enable();
        delete this.dataset.wheelHint;
        return;
      }
      map.dragging.disable();
      // 1本指のときは案内を出す。ピンを押しただけで出ないよう、動いたときに限る。
    }, options);

    container.addEventListener('touchmove', (event) => {
      if (event.touches.length >= 2) return;
      this.dataset.wheelHint = 'true';
      window.clearTimeout(this.#wheelHintTimer);
      this.#wheelHintTimer = window.setTimeout(() => {
        delete this.dataset.wheelHint;
      }, 1400);
    }, options);

    container.addEventListener('touchend', () => {
      if (map.dragging.enabled()) map.dragging.disable();
    }, options);
  }

  /**
   * ホイールでの拡大を修飾キー付きのときだけに限る。
   *
   * @param {any} map
   * @param {HTMLElement} container
   */
  #setupModifierZoom(map, container) {
    const options = { signal: /** @type {AbortSignal} */ (this.#abort?.signal) };
    map.scrollWheelZoom.disable();

    // Windows / Linux は Ctrl、Mac は ⌘。案内の表記を環境に合わせる。
    const isApple = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
    container.dataset.wheelHintText = 'ontouchstart' in window
      ? '指2本で地図を動かせます'
      : isApple
        ? '⌘ を押しながらスクロールすると拡大できます'
        : 'Ctrl を押しながらスクロールすると拡大できます';

    const held = (/** @type {KeyboardEvent | WheelEvent} */ event) => event.metaKey || event.ctrlKey;

    // キーを押している間だけ有効にする。wheel の最中に切り替えると
    // その1回ぶんの拡大が取りこぼされるため、キー側で先に構えておく。
    window.addEventListener('keydown', (event) => {
      if (held(event)) map.scrollWheelZoom.enable();
    }, options);
    window.addEventListener('keyup', () => map.scrollWheelZoom.disable(), options);
    window.addEventListener('blur', () => map.scrollWheelZoom.disable(), options);

    container.addEventListener('wheel', (event) => {
      if (held(event)) return;
      // 修飾キー無しのホイールはページのスクロール。案内だけ出す。
      this.dataset.wheelHint = 'true';
      window.clearTimeout(this.#wheelHintTimer);
      this.#wheelHintTimer = window.setTimeout(() => {
        delete this.dataset.wheelHint;
      }, 1400);
    }, { ...options, passive: true });
  }

  /** 「もっと見る」を押したら次の20件を足す。絞り込みを変えたら先頭に戻る */
  #setupReveal() {
    const button = this.refs.revealButton;
    if (!button) return;

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#revealed += REVEAL_MORE_STEP;
        this.#applyFilters({ resetReveal: false });
      },
      { signal: /** @type {AbortSignal} */ (this.#abort?.signal) }
    );
  }

  #setupGeolocation() {
    const button = this.refs.nearestButton;
    if (!button) return;

    // 非対応のブラウザではボタン自体を出さない（hidden を外さない）
    if (!('geolocation' in navigator) || !navigator.geolocation) return;

    button.hidden = false;
    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#locate();
      },
      { signal: /** @type {AbortSignal} */ (this.#abort?.signal) }
    );

    this.#useGrantedPositionIfAny();
  }

  /**
   * すでに許可されている場合だけ、黙って現在地を使う。
   *
   * 読み込みと同時に許可を求めない。ユーザー操作を起点にしない要求はブラウザに嫌われ、
   * 却下が重なると自動ブロックに入って二度と聞けなくなる。
   * ここで見るのは「もう許可されているか」だけで、`prompt` の状態では何もしない。
   */
  async #useGrantedPositionIfAny() {
    try {
      const permissions = navigator.permissions;
      if (!permissions?.query) return;

      const status = await permissions.query({ name: /** @type {any} */ ('geolocation') });
      if (status.state === 'denied') {
        // 拒否済みならボタンを出しても押せないだけなので隠す
        if (this.refs.nearestButton) this.refs.nearestButton.hidden = true;
        return;
      }
      if (status.state !== 'granted') return;

      this.#locate({ silent: true });
    } catch {
      /* Permissions API が無い環境では何もしない。ボタンから手動で使える */
    }
  }

  /**
   * 押されたときにだけ位置情報を要求する。
   * `silent` は「すでに許可済みなので自動で取りにいく」経路。案内文を出さない。
   *
   * @param {{ silent?: boolean }} [options]
   */
  #locate({ silent = false } = {}) {
    const button = this.refs.nearestButton;
    if (button) button.disabled = true;
    if (!silent) this.#setStatus('現在地を取得しています…');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (button) button.disabled = false;
        this.#origin = { lat: position.coords.latitude, lng: position.coords.longitude };
        this.#originAccuracyM = toFiniteNumber(position.coords.accuracy);
        this.#sortByCurrentPosition(this.#origin);
        this.#renderCurrentPosition();
      },
      (error) => {
        if (button) button.disabled = false;
        // 拒否・失敗・タイムアウトのいずれでも一覧はそのまま残す。現在地も描かない
        this.#setStatus(GEOLOCATION_MESSAGES[error?.code] ?? GEOLOCATION_MESSAGES.default);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }

  /**
   * 現在地を「ソナー」で描く。中心の点と、測位の精度そのものを半径にした円の2枚。
   *
   * 点だけを置かないのが肝。PC の測位は数km〜数十kmずれるので、点1つだと
   * 「ここにいる」と言い切ってしまう。広がる円にすることで「このあたり」までしか主張しない。
   * 地図がまだ無い（SP で一覧だけ・初期化前）ときは何もしない。地図ができた時点で描き直す。
   */
  #renderCurrentPosition() {
    const L = /** @type {any} */ (window).L;
    const map = this.#map;
    if (!map || !L?.circle || !this.#origin) return;

    for (const layer of [this.#hereCircle, this.#hereMarker]) {
      if (layer) map.removeLayer(layer);
    }

    const { lat, lng } = this.#origin;
    // 精度を表す円は置かない。
    // ベクタータイル（MapLibre）の上に Leaflet の SVG を重ねているため、
    // ズームの最中に変換がずれて縦長の楕円に潰れて見えた（実測）。
    // 誤差はパルスの広がりと、カードに出る「約◯km」で十分に伝わる。

    // 中心の点＋パルス。操作の邪魔をしないよう当たり判定は持たせない
    this.#hereMarker = L.marker([lat, lng], {
      icon: L.divIcon({
        className: CLASSES.here,
        html: CURRENT_POSITION_HTML,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      }),
      interactive: false,
      keyboard: false,
      zIndexOffset: 500,
    }).addTo(map);
  }

  /**
   * 現在地から近い順に並べ替える。
   *
   * @param {{ lat: number, lng: number }} origin
   */
  #sortByCurrentPosition(origin) {
    const ranked = rankByDistance(
      this.#entries.map((entry) => ({
        id: entry.index,
        lat: entry.lat,
        lng: entry.lng,
        mappable: entry.mappable,
        approximate: entry.approximate,
      })),
      origin
    );

    const fragment = document.createDocumentFragment();
    /** @type {StoreEntry[]} */
    const ordered = [];
    let measured = 0;

    for (const row of ranked) {
      const entry = this.#entries[Number(row.id)];
      if (!entry) continue;

      entry.distanceKm = row.distanceKm;
      if (row.distanceKm != null) measured += 1;
      this.#renderDistance(entry);
      ordered.push(entry);
      fragment.appendChild(entry.item);
    }

    this.refs.list.appendChild(fragment);

    // ⚠️ DOM だけ入れ替えて終わらせない。
    // 段階表示（もっと見る）は `#entries` の並びで「先頭から N 件」を出すので、
    // 配列が元の順のままだと、画面に出ているのは最初に読み込んだ10件のまま＝
    // 近い順にしたのに近い店が1件も出てこない（スマホで実際にそうなっていた）。
    this.#entries = ordered;
    this.#entries.forEach((entry, index) => {
      entry.index = index;
    });
    this.#applyFilters();

    this.#setStatus(`現在地から近い順に並べ替えました（${measured}件）。所在地が国までしか分からない店舗は並べ替えの対象外です。`);
  }

  /**
   * カードに「約◯km」を出す。カード側に置き場所が無ければ作る。
   * 器は初期状態で `hidden`（値が無いうちに空行を作らないため）なので、値が入ったら外す。
   *
   * @param {StoreEntry} entry
   */
  #renderDistance(entry) {
    const label = formatDistanceLabel(entry.distanceKm);
    let element = /** @type {HTMLElement | null} */ (entry.card.querySelector('[data-store-distance]'));

    if (!label) {
      if (element) {
        element.textContent = '';
        element.hidden = true;
      }
      return;
    }

    if (!element) {
      element = document.createElement('p');
      element.className = CLASSES.distance;
      element.setAttribute('data-store-distance', '');
      entry.card.appendChild(element);
    }

    element.textContent = label;
    element.hidden = false;
  }

  /**
   * @param {string} message
   */
  #setStatus(message) {
    if (this.refs.status) this.refs.status.textContent = message;
  }

  /* -------------------------------------------------- 地図 */

  /**
   * SP の「地図で見る」。押されるまで地図を出さない（一覧だけで1画面に収めるため）。
   * PC では CSS がこのボタンを隠すので、押されることはない。
   */
  #setupMapToggle() {
    const button = this.refs.mapToggle;
    if (!button) return;

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        this.#toggleMapView();
      },
      { signal: /** @type {AbortSignal} */ (this.#abort?.signal) }
    );
  }

  /** 地図と一覧を入れ替える。縦に足さないので、セクションの高さは変わらない。 */
  #toggleMapView() {
    const opened = this.dataset.mapView !== 'map';
    this.dataset.mapView = opened ? 'map' : 'list';

    const button = this.refs.mapToggle;
    if (button) {
      button.setAttribute('aria-expanded', opened ? 'true' : 'false');
      button.textContent = opened ? MAP_TOGGLE_LABELS.close : MAP_TOGGLE_LABELS.open;
    }

    if (!opened) return;

    // 押されたこの瞬間に初めて Leaflet を読み込む（PC の IntersectionObserver と同じ遅延）
    this.#startMap();

    // 直前まで display:none だったので、寸法が確定してから描き直す
    requestAnimationFrame(() => {
      this.#map?.invalidateSize();
      this.#applyPanelOffset();
    });
  }

  /** 画面に入るまで Leaflet を読み込まない */
  #observeMapViewport() {
    const target = this.refs.map;
    if (!target) return;

    if (!('IntersectionObserver' in window)) {
      this.#startMap();
      return;
    }

    this.#mapObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#mapObserver?.disconnect();
        this.#mapObserver = null;
        this.#startMap();
      },
      { rootMargin: '200px' }
    );

    this.#mapObserver.observe(target);
  }

  async #startMap() {
    if (this.#mapStarted) return;
    this.#mapStarted = true;

    const container = this.refs.map;
    const tileUrl = (this.dataset.tileUrl ?? '').trim();
    const tileAttribution = (this.dataset.tileAttribution ?? '').trim();

    // タイルの配信元か著作権表示が空なら、地図は出さずに一覧だけ残す
    if (!container || !tileUrl || !tileAttribution) {
      this.#markMapUnavailable('missing-tile-settings');
      return;
    }

    try {
      const base = this.dataset.assetBase || import.meta.url;
      const assetUrl = (/** @type {string} */ name) => new URL(name, base).href;

      await Promise.all([
        loadStylesheet(assetUrl(VENDOR_ASSETS.leafletCss)),
        loadStylesheet(assetUrl(VENDOR_ASSETS.clusterCss)),
      ]);
      await loadScript(assetUrl(VENDOR_ASSETS.leafletJs));
      await loadScript(assetUrl(VENDOR_ASSETS.clusterJs));

      // MapLibre は約900KB ある。ベクターを指定しているときだけ読む。
      if (isVectorStyleUrl(tileUrl)) {
        await loadStylesheet(assetUrl(VENDOR_ASSETS.maplibreCss));
        await loadScript(assetUrl(VENDOR_ASSETS.maplibreJs));
        await loadScript(assetUrl(VENDOR_ASSETS.maplibreLeafletJs));
      }

      const L = /** @type {any} */ (window).L;
      if (!L?.map) throw new Error('store-locator: Leaflet did not initialise');

      this.#createMap(L, container, tileUrl, tileAttribution);
    } catch (error) {
      // 地図が死んでも一覧と絞り込みは生かす
      console.warn('store-locator: map initialisation failed', error);
      this.#markMapUnavailable('error');
    }
  }

  /**
   * @param {string} reason
   */
  #markMapUnavailable(reason) {
    this.dataset.mapState = reason;
    if (this.refs.map) this.refs.map.hidden = true;

    // 出せない地図への切り替えボタンは残さない（押しても何も出ない状態を作らない）
    this.dataset.mapView = 'list';
    const button = this.refs.mapToggle;
    if (button) {
      button.hidden = true;
      button.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * @param {any} L
   * @param {HTMLElement} container
   * @param {string} tileUrl
   * @param {string} tileAttribution
   */
  #createMap(L, container, tileUrl, tileAttribution) {
    const center = parseLatLng(this.dataset.initialCenter) ?? FALLBACK_CENTER;
    const zoom = clamp(toFiniteNumber(this.dataset.initialZoom) ?? FALLBACK_ZOOM, 1, 18);

    // 最大ズームは配信元で違う（地理院タイル18 / OpenStreetMap 19 / ベクターは20前後）。
    // 地図そのものに持たせる。ラスターなら L.tileLayer が設定してくれるが、
    // ベクターのときは誰も設定せず maplibre-gl-leaflet が
    // 「Map has no maxZoom specified」で落ちる（実測）。
    const parsedMaxZoom = Number.parseInt(this.dataset.maxZoom ?? '', 10);
    const maxZoom = Number.isFinite(parsedMaxZoom) && parsedMaxZoom > 0 ? parsedMaxZoom : 18;

    const map = L.map(container, {
      center: [center.lat, center.lng],
      zoom,
      maxZoom,
      // ページを読む途中で地図が拡大してしまわないよう、触られるまでホイールは無効にする
      scrollWheelZoom: false,
      // 左右の複製世界（ピンの無いアメリカ）へ流れていかないようにする
      maxBounds: MAP_BOUNDS,
      maxBoundsViscosity: 1,
    });

    // MapLibre（背景）は、縦に世界が収まる倍率より引けない。Leaflet（ピン）だけがさらに引けると、
    // 背景が止まったままピンだけ縮んで中央へ寄り、数字の円が本来の位置からずれる（#600・実測）。
    // Leaflet 側も「範囲が画面を覆う倍率」までに止める。画面の大きさで変わるので、変わるたびに測り直す
    const fitMinZoom = () => {
      const fit = map.getBoundsZoom(L.latLngBounds(MAP_BOUNDS), true);
      if (Number.isFinite(fit)) map.setMinZoom(Math.ceil(fit));
    };
    fitMinZoom();
    map.on('resize', fitMinZoom);

    // ホイールは常にページのスクロール。⌘（Mac）か Ctrl を押している間だけ地図が拡大する。
    // Google マップと同じ作法。これが無いと、ページを読み進める途中で地図の上を通った
    // だけで拡大してしまい、スクロールが奪われる。
    this.#setupModifierZoom(map, container);
    this.#setupTwoFingerPan(map, container);

    // 拡大縮小は右上へ。左上は一覧のパネルが浮いていて押せない（PC）
    map.zoomControl?.setPosition('topright');

    // 右下の出典表示から「🇺🇦 Leaflet」の接頭辞を外す。地図データの出典（配信元の条件）だけ残す
    map.attributionControl?.setPrefix(false);

    if (isVectorStyleUrl(tileUrl)) {
      // ベクタータイル。MapLibre が描いたものを Leaflet の1レイヤーとして載せる。
      // ピン・クラスタ・吹き出しは Leaflet のままなので、ここ以外は何も変わらない。
      L.maplibreGL({ style: tileUrl, attribution: tileAttribution }).addTo(map);
    } else {
      L.tileLayer(tileUrl, { attribution: tileAttribution, maxZoom }).addTo(map);
    }

    // ピンをまとめるかどうかはセクション設定で切り替える。
    // 国内430件ならまとめなくても Leaflet は素でさばけるので、既定はまとめない
    // （まとめると1本ずつの位置が見えず、どこに店があるか掴みにくい）。
    //
    // ⚠️ まとめない場合も入れ物は markerClusterGroup のまま、半径を 0 にして「まとまらない」状態にする。
    // 素の LayerGroup に替えると addLayers / getVisibleParent が無く、絞り込みの反映で必ず落ちる
    // （実測: data-map-state="error" で地図が丸ごと消えた）。半径 0 は DistanceGrid が
    // 同じ画素に完全一致したときしかまとめないので、実質まとまらない。
    const useCluster = this.dataset.cluster === 'true';
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: useCluster ? 60 : 0,
      chunkedLoading: true,
    });

    for (const entry of this.#entries) {
      // 座標を持たない店舗はピンを立てない。海外32件は国の代表点にだけ立てる
      if (!entry.mappable || entry.lat == null || entry.lng == null) continue;

      const marker = L.marker([entry.lat, entry.mapLng], {
        icon: L.divIcon({
          className: entry.approximate ? `${CLASSES.pin} ${CLASSES.pinApproximate}` : CLASSES.pin,
          html: PIN_HTML,
          iconSize: [PIN_SIZE.width, PIN_SIZE.height],
          iconAnchor: [PIN_SIZE.anchorX, PIN_SIZE.anchorY],
          popupAnchor: [0, -PIN_SIZE.anchorY],
        }),
        title: entry.name,
        alt: entry.name,
        riseOnHover: true,
      });

      marker.bindPopup(this.#createPopupContent(entry), {
        autoClose: true,
        closeOnClick: true,
        maxWidth: 260,
      });

      entry.marker = marker;
      this.#entryByMarker.set(marker, entry);
    }

    cluster.on('click', (event) => this.#handleMarkerClick(event.layer));
    map.on('popupclose', (event) => {
      if (this.#openPopupMarker?.getPopup?.() === event.popup) this.#openPopupMarker = null;
    });

    map.addLayer(cluster);

    this.#map = map;
    this.#cluster = cluster;
    this.dataset.mapState = 'ready';

    this.#syncMarkers();
    // 地図より先に現在地が取れていたら、ここで描く
    this.#renderCurrentPosition();

    // 浮かせたパネルや切り替え直後は、初期化時の寸法が確定していないことがある
    requestAnimationFrame(() => {
      map.invalidateSize();
      this.#applyPanelOffset();
      this.#fitToMarkers();
    });
  }

  /**
   * ピンが全部入るように寄せる（`data-fit-markers="true"` のときだけ）。
   *
   * TOP の帯は同じ器を PC 656px / SP 247px で使う。固定のズームは片方にしか合わず、
   * 広いほうでは日本が小さく余白だらけになり、狭いほうでは沖縄と北海道が見切れる。
   * 寸法が確定してから実際のピンの範囲に合わせる。
   *
   * 一覧のあるページでは呼ばない。訪問者が動かした地図が絞り込みのたびに戻ってしまう。
   */
  #fitToMarkers() {
    if (this.dataset.fitMarkers !== 'true') return;

    const map = this.#map;
    const cluster = this.#cluster;
    if (!map || !cluster) return;

    const L = /** @type {any} */ (window).L;
    if (!L) return;

    // 海外のピンは国の代表点なので、混ぜると帯が世界地図になる。実所在地のピンだけで決める
    const points = this.#entries
      .filter((entry) => entry.marker && !entry.approximate && entry.lat != null && entry.lng != null)
      .map((entry) => [entry.lat, entry.mapLng]);

    const bounds = points.length ? L.latLngBounds(points) : cluster.getBounds();
    if (!bounds || !bounds.isValid()) return;

    map.fitBounds(bounds, { padding: [24, 24], animate: false });
  }

  /**
   * 一覧のパネルが地図に重なっている幅（px）。重なっていなければ 0。
   * PC は地図の上に不透明なパネルを浮かせるので、そのぶん中身が隠れる。
   *
   * メディアクエリを JS 側に二重定義せず、実際の位置関係から測る。
   * SP（一覧と地図を入れ替える）では重ならないので自動的に 0 になる。
   *
   * @returns {number}
   */
  #panelOffsetX() {
    const container = this.refs.map;
    const panel = this.refs.list;
    if (!container || !panel) return 0;

    const map = container.getBoundingClientRect();
    const rect = panel.getBoundingClientRect();
    if (!map.width || !rect.width) return 0;

    const overlapX = Math.min(rect.right, map.right) - Math.max(rect.left, map.left);
    const overlapY = Math.min(rect.bottom, map.bottom) - Math.max(rect.top, map.top);
    if (overlapX <= 0 || overlapY <= 0) return 0;

    // 地図の半分以上を補正に使うと、逆に端が見えなくなる
    return Math.min(overlapX + 16, map.width * 0.45);
  }

  /**
   * パネルに隠れるぶん、地図の中身を右へ寄せる。
   * 設定の初期中心をパネルの裏ではなく「見えている側の中央」に置くための補正で、初期化時の1回だけ。
   */
  #applyPanelOffset() {
    if (this.#panelOffsetApplied || !this.#map) return;

    const offset = this.#panelOffsetX();
    if (offset <= 0) return;

    this.#panelOffsetApplied = true;
    this.#map.panBy([-offset / 2, 0], { animate: false });
  }

  /**
   * @param {StoreEntry} entry
   * @returns {HTMLElement}
   */
  #createPopupContent(entry) {
    const wrapper = document.createElement('div');
    wrapper.className = 'store-locator-map-yokomo__popup';

    const name = document.createElement('strong');
    name.textContent = entry.name || entry.card.dataset.searchText || '';
    wrapper.appendChild(name);

    // 住所はカードの本文から拾う（JSON は持っていない）。無いカードでは吹き出しに店名だけ出る
    const address = entry.card.querySelector(`.${CLASSES.address}`)?.textContent?.trim();
    if (address) {
      const line = document.createElement('span');
      line.textContent = address;
      line.style.display = 'block';
      wrapper.appendChild(line);
    }

    // 国の代表点に立てたピンは、その場所に店があるわけではない。黙って出すと誤解される
    if (entry.approximate) {
      const note = document.createElement('span');
      note.className = 'store-locator-map-yokomo__popup-note';
      note.textContent = 'この国にある店舗です（正確な所在地は未登録）';
      wrapper.appendChild(note);
    }

    return wrapper;
  }

  /**
   * @param {any} marker
   */
  #handleMarkerClick(marker) {
    const entry = this.#entryByMarker.get(marker);
    if (!entry) return;

    // 吹き出しは同時に1つだけ。Leaflet の既定でもそうなるが、ここで明示的に閉じておく
    if (this.#openPopupMarker && this.#openPopupMarker !== marker) this.#openPopupMarker.closePopup();
    this.#openPopupMarker = marker;

    // 吹き出しが浮かせたパネルの裏に出ないよう、自動スクロールの余白にもパネル幅を渡す
    const popup = marker.getPopup?.();
    if (popup) popup.options.autoPanPaddingTopLeft = [this.#panelOffsetX() + 16, 16];

    this.#setActiveEntry(entry);
    this.#scrollToEntry(entry);

    // 地図の中心は動かさない（押すたびに飛んで戻る動きを作らない）
    marker.openPopup();
  }

  /** 絞り込みの結果に地図のピンを合わせる */
  #syncMarkers() {
    if (!this.#cluster) return;

    const shown = this.#entries.filter((entry) => entry.visible && entry.marker);
    const markers = shown.map((entry) => entry.marker);

    this.#cluster.clearLayers();
    if (markers.length) this.#cluster.addLayers(markers);

    const force = this.#fitVisibleOnce;
    this.#fitVisibleOnce = false;
    this.#fitVisible(shown, force);
  }

  /**
   * 絞り込んだ結果へ地図を寄せる。
   *
   * 寄せるのは2つの場合だけ。
   * 1. 選択肢（地域・都道府県・ジャンル・種別）を変えたとき。はっきりした操作なので寄せる。
   *    「海外」を選ぶとピンは日本の外にあり、寄せないと画面は日本のまま＝何も起きて
   *    いないように見える。
   * 2. 結果が1件も画面に入っていないとき。
   *
   * キーワードの打鍵では寄せない。1文字ごとに地図が飛ぶと、訪問者が自分で動かした
   * 位置が毎回失われる。
   *
   * 寄せ先は**実所在地のピンだけ**で決める。海外は国の代表点なので、国内の結果に
   * 混ざっていると寄せ先が世界地図になる。海外だけに絞ったときはそれで決める。
   *
   * @param {StoreEntry[]} shown 画面に出ている店舗（ピンを持つものだけ）
   * @param {boolean} force 選択肢を変えた直後か
   */
  #fitVisible(shown, force) {
    const map = this.#map;
    const L = /** @type {any} */ (window).L;
    if (!map || !L || !shown.length) return;

    // 器がまだ寸法を持っていないとき（SP で地図を出す前など）の getBounds は当てにならない。
    // ここで寄せると、地図を開いた瞬間に世界地図が出る
    const size = map.getSize();
    if (!size || !size.x || !size.y) return;

    const precise = shown.filter((entry) => !entry.approximate);
    const target = precise.length ? precise : shown;

    const points = target
      .filter((entry) => entry.lat != null && entry.lng != null)
      .map((entry) => [entry.lat, entry.mapLng]);
    if (!points.length) return;

    const view = map.getBounds();
    if (!force && points.some(([lat, lng]) => view.contains(L.latLng(lat, lng)))) return;

    map.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 8, animate: false });
  }
}

/* ------------------------------------------------------------------ *
 * 小物
 * ------------------------------------------------------------------ */

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function toFiniteNumber(value) {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `"36.2048,138.2529"` を座標にする。
 *
 * @param {string | undefined} value
 * @returns {{ lat: number, lng: number } | null}
 */
function parseLatLng(value) {
  if (!value) return null;

  const [rawLat, rawLng] = String(value).split(',');
  const lat = toFiniteNumber(rawLat);
  const lng = toFiniteNumber(rawLng);

  return isValidPoint({ lat, lng }) ? { lat: /** @type {number} */ (lat), lng: /** @type {number} */ (lng) } : null;
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

if (typeof customElements !== 'undefined' && !customElements.get('store-locator-yokomo-component')) {
  customElements.define('store-locator-yokomo-component', StoreLocatorYokomoComponent);
}
